/**
 * Date Utility Functions for YearlyCalendar Component
 * Uses date-fns for date manipulation
 */
import {
  startOfDay,
  startOfMonth,
  endOfMonth,
  addDays,
  subDays,
  addMonths,
  subMonths,
  addYears,
  subYears,
  getDay,
  getWeek,
  format,
  isToday,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  differenceInDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
} from 'date-fns';

/**
 * Generate a unique key for a date in "YYYY_M_D" format
 */
export const getDateKey = (date: Date): string => {
  return `${date.getFullYear()}_${date.getMonth()}_${date.getDate()}`;
};

/**
 * Generate a unique DOM ID for a date
 */
export const getDateUid = (prefix: string, date: Date): string => {
  return `${prefix}-${getDateKey(date)}`;
};

/**
 * Matches a pure date-only string ("YYYY-MM-DD") with no time component.
 */
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a date value into a Date using LOCAL calendar semantics for date-only
 * strings. `new Date("2026-08-04")` parses as UTC midnight, which shifts one day
 * west of UTC (e.g. Aug 3 in Pacific time). Values with a time component keep
 * their absolute-instant semantics.
 */
export const parseDate = (date: Date | string): Date => {
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === 'string') {
    const trimmed = date.trim();
    const match = DATE_ONLY_REGEX.exec(trimmed);
    if (match) {
      const [year, month, day] = trimmed.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
  }
  return new Date(date);
};

/**
 * Normalize a date to midnight (start of day)
 */
export const normalizeDate = (date: Date | string): Date => {
  return startOfDay(parseDate(date));
};

/**
 * Resolve the last calendar day an event actually occupies.
 * Ends that fall exactly on midnight are treated as EXCLUSIVE boundaries
 * (all-day / holiday events span to the following day's 00:00), so the event is
 * painted only through the previous day - mirroring the legacy calendar which
 * subtracts a second from all-day ends. Pure date-only strings ("YYYY-MM-DD")
 * denote an inclusive calendar day and are returned unchanged.
 */
export const getInclusiveEndDate = (endDate: Date | string): Date => {
  if (typeof endDate === 'string' && DATE_ONLY_REGEX.test(endDate.trim())) {
    return parseDate(endDate);
  }
  const parsed = parseDate(endDate);
  const isMidnight =
    parsed.getHours() === 0 &&
    parsed.getMinutes() === 0 &&
    parsed.getSeconds() === 0 &&
    parsed.getMilliseconds() === 0;
  return isMidnight ? subDays(parsed, 1) : parsed;
};

/**
 * Get the timestamp for a normalized date
 */
export const getNormalizedTimestamp = (date: Date | string): number => {
  return normalizeDate(date).getTime();
};

/**
 * Calculate the first visible day in a month view calendar
 * Takes into account the starting day of the week
 */
export const getFirstVisibleDay = (date: Date, startingDay: number = 0): Date => {
  const firstOfMonth = startOfMonth(date);
  const dayOfWeek = getDay(firstOfMonth);
  const daysFromPrevMonth = (dayOfWeek - startingDay + 7) % 7;
  return subDays(firstOfMonth, daysFromPrevMonth);
};

/**
 * Generate an array of dates for a calendar month view (42 days = 6 weeks)
 */
export const generateMonthDays = (date: Date, startingDay: number = 0): Date[] => {
  const firstVisible = getFirstVisibleDay(date, startingDay);
  const days: Date[] = [];
  
  for (let i = 0; i < 42; i++) {
    days.push(addDays(firstVisible, i));
  }
  
  return days;
};

/**
 * Generate day header labels (Sun, Mon, Tue, etc.)
 */
export const generateDayHeaders = (startingDay: number = 0, formatStr: string = 'EEE'): string[] => {
  const baseDate = new Date(2024, 0, 7); // A Sunday
  const headers: string[] = [];
  
  for (let i = 0; i < 7; i++) {
    const dayIndex = (startingDay + i) % 7;
    const date = addDays(baseDate, dayIndex);
    headers.push(format(date, formatStr));
  }
  
  return headers;
};

/**
 * Get all days between two dates (inclusive)
 */
export const getDaysBetween = (startDate: Date | string, endDate: Date | string): Date[] => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);

  // eachDayOfInterval throws when end precedes start (e.g. a zero-length event
  // whose exclusive midnight end resolves before its start day).
  if (end.getTime() < start.getTime()) {
    return [start];
  }

  return eachDayOfInterval({ start, end });
};

/**
 * Calculate the number of days between two dates
 */
export const getDayCount = (startDate: Date | string, endDate: Date | string): number => {
  return differenceInDays(normalizeDate(endDate), normalizeDate(startDate)) + 1;
};

/**
 * Check if a date is within a range
 */
export const isDateInRange = (date: Date, minDate?: Date, maxDate?: Date): boolean => {
  if (!minDate && !maxDate) return true;
  
  const normalized = normalizeDate(date);
  
  if (minDate && normalized < normalizeDate(minDate)) return false;
  if (maxDate && normalized > normalizeDate(maxDate)) return false;
  
  return true;
};

/**
 * Check if a date is a weekend
 */
export const isWeekend = (date: Date): boolean => {
  const day = getDay(date);
  return day === 0 || day === 6; // Sunday = 0, Saturday = 6
};

/**
 * Check if a date is a non-work day based on configuration
 */
export const isNonWorkDay = (date: Date, nonWorkDays: string[] = []): boolean => {
  const dayName = format(date, 'EEEE'); // Full day name
  return nonWorkDays.includes(dayName);
};

/**
 * Format a date for display
 */
export const formatDate = (date: Date, formatStr: string): string => {
  return format(date, formatStr);
};

/**
 * Navigation helpers
 */
export const navigateMonth = (date: Date, direction: 1 | -1): Date => {
  return direction === 1 ? addMonths(date, 1) : subMonths(date, 1);
};

export const navigateYear = (date: Date, direction: 1 | -1): Date => {
  return direction === 1 ? addYears(date, 1) : subYears(date, 1);
};

/**
 * Get week number for a date
 */
export const getWeekNumber = (date: Date): number => {
  return getWeek(date);
};

/**
 * Check if two dates are the same day
 */
export { isToday, isSameDay, isSameMonth };

/**
 * Date-fns re-exports for convenience
 */
export {
  startOfDay,
  startOfMonth,
  endOfMonth,
  addDays,
  subDays,
  addMonths,
  subMonths,
  addYears,
  subYears,
  getDay,
  format,
  differenceInCalendarDays,
  startOfWeek,
  endOfWeek,
};
